import React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {useDocumentClickListener} from '../../../hooks';
import {Icon} from '../../icons';
import {IconNames} from '../../icons/types';
import {Layout} from '../../layout';
import {Ruler} from '../../ruler';
import {Spacer} from '../../spacer';
import {Text} from '../../text';
import {Dropdown} from '../Dropdown';
import {SimpleDropdownItem} from './SimpleDropdownItem';
import {SimpleDropdownProps} from '../types';
import {BadgeOrIcon} from './BadgeOrIcon';

const EmptyIcons: IconNames[] = [];
type ItemWithID = {id: number; value: string};

export const Readonly: React.FC<Omit<SimpleDropdownProps, 'readonly'>> = ({
  items,
  favourites,
  icons = EmptyIcons,
  placeholder,
  selectedIndex,
  onSelectedIndexChange,
  onRenderBadge,
}) => {
  // State
  const [expanded, setExpanded] = useState<boolean>();

  // Make sure we have an "id" for the dropdown
  const itemsWithId = useMemo(
    (): ItemWithID[] => items.map((itm, index) => ({id: index, value: itm})),
    [items],
  );

  // Favourites
  const favouriteIndicies = useMemo(() => {
    return favourites?.map(f => itemsWithId[f].id);
  }, [favourites, itemsWithId]);

  // Handlers
  const handleSelection = useCallback(
    (index: number) => {
      setExpanded(false);
      onSelectedIndexChange && onSelectedIndexChange(index);
    },
    [onSelectedIndexChange],
  );

  const toggleExpanded = useCallback(() => setExpanded(p => !p), []);
  const closeDrowdown = useCallback(() => setExpanded(false), []);

  // render item
  const renderItem = useCallback(
    (_, index: number) => {
      // This is where we filter out searchable items
      return (
        <SimpleDropdownItem
          index={index}
          title={items[index]}
          badge={
            <BadgeOrIcon
              index={index}
              onRenderBadge={onRenderBadge}
              icons={icons}
            />
          }
          isActive={index === selectedIndex}
          onSelectedIndexChange={handleSelection}
        />
      );
    },
    [items, onRenderBadge, icons, selectedIndex, handleSelection],
  );

  const renderSeparator = useCallback(() => <Ruler padding="6" />, []);

  // Handle dismissal of menu when clicking outside menu
  useDocumentClickListener(expanded ?? false, closeDrowdown);

  return useMemo(
    () => (
      <Layout vertical height="44">
        <Layout
          borderColor="border_mono_30"
          borderWidth="1"
          borderStyle="solid"
          borderRadius="4"
          paddingHorizontal="6"
          height={44}
          align="center"
          onPress={toggleExpanded}
          focusBorder={expanded ? 'on' : undefined}>
          <Layout horizontal flex>
            <Spacer width="10" />
            {/** Selected Badge or Icon */}
            {
              <BadgeOrIcon
                index={selectedIndex}
                onRenderBadge={onRenderBadge}
                icons={icons}
              />
            }
            {/** Selected title */}
            <Layout horizontal flex align="center">
              {selectedIndex !== undefined && selectedIndex !== -1 ? (
                <Text variant={'inline_14_default'}>
                  {items[selectedIndex]}
                </Text>
              ) : placeholder ? (
                <Text
                  variant="inline_14_default"
                  textColor="content_mono_secondary">
                  {placeholder}
                </Text>
              ) : null}
            </Layout>
            {/** Arrow down */}
            <Layout align="center" horizontal>
              <Icon name="arrowDownSmall" size="24" />
              <Spacer width="8" />
            </Layout>
          </Layout>
        </Layout>
        <Layout>
          <Dropdown
            state={expanded ? 'expanded' : 'collapsed'}
            items={itemsWithId}
            filterOutFavourites
            renderFavouritesSeparator={renderSeparator}
            favourites={favouriteIndicies}
            renderItemContent={renderItem}
          />
        </Layout>
      </Layout>
    ),
    [
      toggleExpanded,
      expanded,
      selectedIndex,
      onRenderBadge,
      icons,
      items,
      placeholder,
      itemsWithId,
      renderSeparator,
      favouriteIndicies,
      renderItem,
    ],
  );
};
